import { useCallback, useMemo } from 'react';

import {
  useRfqBuilderActions,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';

import {
  useRfqBuilderFutureLegView,
  useRfqBuilderOptionLegView,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/leg-view';

import TransparentSelectField from '#/components/UnifiedRfqs/components/TransparentSelectField';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { useTrackAction as useBaseTrackAction } from '@paradigm/features/src/analytics';
import { FormOption } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/types';

export function FutureLegExpirationSelector() {
  const { index, expiration, isHedge } = useRfqBuilderFutureLegView();
  const { selectFutureLegExpiration, changeLegPrice } = useRfqBuilderActions();
  const isMobile = useIsMobile();
  const trackAction = useTrackAction(isHedge);

  const handleOnChange = useCallback(
    (option: FormOption<number | null> | null) => {
      trackAction(option?.value);

      if (option != null) {
        selectFutureLegExpiration(index, option.value);
        if (isHedge) {
          changeLegPrice(index, null);
        }
      }
    },
    [changeLegPrice, index, isHedge, selectFutureLegExpiration, trackAction],
  );

  return useMemo(
    () => (
      <TransparentSelectField
        aria-label="Select expiration"
        options={expiration.options}
        value={expiration.selectedOption}
        invalid={!expiration.isValid}
        onChange={handleOnChange}
        isSearchable={!isMobile}
      />
    ),
    [
      expiration.options,
      expiration.selectedOption,
      expiration.isValid,
      handleOnChange,
      isMobile,
    ],
  );
}

export function OptionLegExpirationSelector() {
  const { index, expiration } = useRfqBuilderOptionLegView();
  const { selectOptionLegExpiration } = useRfqBuilderActions();
  const isMobile = useIsMobile();
  const trackAction = useTrackAction();

  const handleOnChange = useCallback(
    (option: FormOption<number | null> | null) => {
      trackAction(option?.value);

      if (option?.value != null) selectOptionLegExpiration(index, option.value);
    },
    [index, selectOptionLegExpiration, trackAction],
  );

  return useMemo(
    () => (
      <TransparentSelectField
        aria-label="Select expiration"
        options={expiration.options}
        value={expiration.selectedOption}
        invalid={!expiration.isValid}
        isDisabled={expiration.isDisabled}
        onChange={handleOnChange}
        isSearchable={!isMobile}
      />
    ),
    [
      expiration.isDisabled,
      expiration.isValid,
      expiration.options,
      expiration.selectedOption,
      handleOnChange,
      isMobile,
    ],
  );
}

function useTrackAction(isHedge?: boolean) {
  const trackAction = useBaseTrackAction();
  const { strategy, strategyShortcut } = useRfqBuilderView();
  const strategyCode =
    strategy.selectedOption?.value ??
    strategyShortcut.selectedOption?.value ??
    null;

  return useCallback(
    (expiry: number | null | undefined) => {
      trackAction('update strategy leg', {
        strategyCode,
        expiry,
        isHedge,
      });
    },
    [strategyCode, trackAction, isHedge],
  );
}
